import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Backdrop, Fade } from "@mui/material";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { ParentGateButton } from "features/parent";
import { NotificationBanner } from "features/notification";
import { IconContact, IconFeedback } from "./assets";
import { TrialNoticeSection } from "./TrialNoticeSection";

export const UniversalPanel = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const navigate = useNavigate();
  const handleDialog = useDialog();
  const universal = useSelector((state: RootState) => state.config.dialog.universal);

  const handleClose = () => {
    handleDialog({ type: DialogType.UNIVERSAL, value: false });
  };

  const handleClick = (path: string) => () => {
    btnDelay(() => {
      play();
      handleClose();
      navigate(path);
    });
  };

  const handleOpenContact = () => {
    btnDelay(() => {
      play();
      handleClose();
      handleDialog({ type: DialogType.CONTACT, value: true });
    });
  };

  const handleOpenReview = () => {
    btnDelay(() => {
      play();
      handleClose();
      handleDialog({ type: DialogType.RATING, value: true });
    });
  };

  const handleOpenAssistant = () => {
    btnDelay(() => {
      play();
      handleClose();
      handleDialog({ type: DialogType.ASSISTANT, value: true });
    });
  };

  return (
    <Backdrop
      open={universal.value}
      sx={{
        zIndex: 100000,
        backgroundColor: "transparent",
        opacity: 1,
        backdropFilter: "blur(8px)",
      }}
    >
      <Fade in>
        <div
          onClick={handleClose}
          className="absolute inset-0 flex-col-el flex-center bg-textcolor-black/50 pointer-events-auto"
        >
          <div className="w-full h-full flex-col-center" style={{ transform: `scale(${scale})` }}>
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex-col-view items-center pointer-events-auto gap-6"
            >
              <div className="flex-row-view justify-start items-start">
                <div
                  className="flex-col-center cursor-pointer mx-[40px]"
                  onClick={handleClick("/top")}
                >
                  <img
                    alt=""
                    src={`${Constants.assetHost}/assets/images/app_top_capture.webp`}
                    onError={(e) =>
                      // @ts-expect-error e.target is EventTarget without a typed src property
                      (e.target.src = `${Constants.assetHost}/assets/images/app_top_capture.png`)
                    }
                    className="flex-col-view w-[416px] h-[312px] border-solid border-white border-[12px] rounded-[8px]"
                  />
                  <p className="text text-white !text-[28px] !leading-[45px]">
                    {I18n.t("MSG_SETTING_GO_TO_TOP")}
                  </p>
                </div>

                <div className="flex-col-view w-[450px] h-[336px] mr-[40px] gap-[16px]">
                  <div className="flex-row-view gap-[16px] h-[160px]">
                    <button
                      className="flex-1 h-full flex-col-el flex-center cursor-pointer bg-orange-100 border-solid border-white border-[8px] rounded-[8px] active:opacity-70"
                      onClick={handleOpenContact}
                    >
                      <div className="flex-col-center">
                        <div className="flex-col-center" style={{ width: 80, height: 60 }}>
                          <IconContact />
                        </div>
                        <p className="text text-white !text-[20px] !leading-[28px] !mt-2">
                          {I18n.t("MSG_SETTING_GO_TO_CONTACT")}
                        </p>
                      </div>
                    </button>

                    <button
                      className="flex-1 h-full flex-col-el flex-center cursor-pointer bg-orange-100 border-solid border-white border-[8px] rounded-[8px] active:opacity-70"
                      onClick={handleOpenReview}
                    >
                      <div className="flex-col-center">
                        <div className="flex-col-center" style={{ width: 80, height: 60 }}>
                          <IconFeedback />
                        </div>
                        <p className="text text-white !text-[20px] !leading-[28px] !mt-2">
                          {I18n.t("MSG_SETTING_GO_TO_REVIEW")}
                        </p>
                      </div>
                    </button>
                  </div>

                  <button
                    className="h-[160px] flex-col-el flex-center cursor-pointer bg-[#34d399] border-solid border-white border-[8px] rounded-[8px] active:opacity-70 !p-0"
                    onClick={handleOpenAssistant}
                  >
                    <div className="flex flex-row items-center justify-start h-full w-full overflow-hidden">
                      <img
                        alt="まなパルくん"
                        src={`${Constants.assetHost}/assets/images/little_pal.png`}
                        className="h-full w-auto object-contain"
                      />
                      <p className="text text-white !text-[24px] !leading-[32px] !ml-3">
                        {I18n.t("MSG_SETTING_GO_TO_ASSISTANT")}
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Close Page Button */}
              <div className="flex-row-el flex-center w-full alpha mt-6">
                <button className="btn btn-blue" onClick={handleClose}>
                  <p className="text-body text-white text-[24px] !mb-[3px]">
                    {I18n.t("MSG_SETTING_CLOSE_BTN")}
                  </p>
                </button>
              </div>
            </div>
          </div>

          <div
            onClick={(e) => e.stopPropagation()}
            className="flex-col-el items-center !absolute pointer-events-auto"
            style={{ top: 11 * scale }}
          >
            <div style={{ transform: `scale(${scale})`, transformOrigin: "top" }}>
              <TrialNoticeSection />
            </div>
          </div>

          <NotificationBanner from="universal" />

          <ParentGateButton />
        </div>
      </Fade>
    </Backdrop>
  );
};
